(() => {
  const STICKY_SELECTOR = '[data-sticky-product-ui][data-sticky-section-id]';

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return String(value).replace(/([ #;?%&,.+*~\\':"!^$\[\]()=>|\/@])/g, '\\$1');
  }

  function isInsideSticky(element) {
    return Boolean(element && element.closest('[data-sticky-product-ui]'));
  }

  function triggerFieldEvents(element) {
    if (!element) return;
    element.dispatchEvent(new Event('input', { bubbles: true }));
    element.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function clampToRules(value, min, max, step) {
    let numericValue = Number(value);
    const numericMin = Number.isFinite(Number(min)) ? Number(min) : 1;
    const numericStep = Number.isFinite(Number(step)) && Number(step) > 0 ? Number(step) : 1;
    const numericMax = max !== null && max !== '' && Number.isFinite(Number(max)) ? Number(max) : null;

    if (!Number.isFinite(numericValue)) numericValue = numericMin;
    if (numericValue < numericMin) numericValue = numericMin;
    if (numericMax !== null && numericValue > numericMax) numericValue = numericMax;

    const offset = numericValue - numericMin;
    const aligned = numericMin + Math.round(offset / numericStep) * numericStep;

    if (numericMax !== null) {
      return Math.min(Math.max(aligned, numericMin), numericMax);
    }

    return Math.max(aligned, numericMin);
  }

  function getButtonLabel(button) {
    if (!button) return '';

    if ('value' in button && typeof button.value === 'string' && button.value.trim()) {
      return button.value.trim();
    }

    const ariaLabel = button.getAttribute('aria-label');
    const clone = button.cloneNode(true);

    clone.querySelectorAll(`
      .loading-overlay__spinner,
      .spinner,
      [hidden],
      script,
      style,
      svg,
      .icon,
      .visually-hidden
    `).forEach((el) => el.remove());

    const text = (clone.textContent || '').replace(/\s+/g, ' ').trim();
    return text || (ariaLabel ? ariaLabel.trim() : '');
  }

  function getStickySubmitTextElement(button) {
    if (!button) return null;

    return (
      button.querySelector('[data-sticky-submit-text]') ||
      Array.from(button.querySelectorAll('span')).find(
        (span) => !span.closest('.loading-overlay__spinner')
      ) ||
      null
    );
  }

  class StickyProductFormProxy {
    constructor(sectionId, parts) {
      this.sectionId = sectionId;
      this.parts = parts;
      this.syncQueued = false;
      this.boundHandleDocumentChange = this.handleDocumentChange.bind(this);
      this.boundHandleDocumentClick = this.handleDocumentClick.bind(this);
      this.boundHandleIntersection = this.handleIntersection.bind(this);
      this.submitVisibilityObserver = null;
      this.observedSubmitButton = null;
    }

    init() {
      if (!this.getMainForm()) return;

      this.bindStickyVariantControls();
      this.bindStickyQuantityControls();
      this.bindStickyButtons();
      this.observeMainSection();
      this.observeStickyRoots();
      this.bindDocumentEvents();
      this.observeMainSubmitButtonVisibility();
      this.syncFromMain();
    }

    getMainForm() {
      const formId = this.getMainFormId();
      return formId ? document.getElementById(formId) : null;
    }

    getMainFormId() {
      const source = this.parts.variant || this.parts.quantity || this.parts.buy;
      return source ? source.dataset.mainProductFormId : null;
    }

    getMainSection() {
      return document.getElementById(`MainProduct-${this.sectionId}`);
    }

    getMainVariantRadios() {
      const el = document.getElementById(`variant-radios-${this.sectionId}`);
      return isInsideSticky(el) ? null : el;
    }

    getMainVariantSelects() {
      const el = document.getElementById(`variant-selects-${this.sectionId}`);
      return isInsideSticky(el) ? null : el;
    }

    getMainQuantityInput() {
      const selector = `#Quantity-${cssEscape(this.sectionId)}`;
      return Array.from(document.querySelectorAll(selector)).find((el) => !isInsideSticky(el)) || null;
    }

    getMainSubmitButton() {
      const selector = `#ProductSubmitButton-${cssEscape(this.sectionId)}`;
      return Array.from(document.querySelectorAll(selector)).find((el) => !isInsideSticky(el)) || null;
    }

    getMainErrorWrapper() {
      const mainForm = this.getMainForm();
      if (!mainForm) return null;
      return mainForm.closest('product-form, .product-form')?.querySelector('.product-form__error-message-wrapper') || null;
    }

    getMainPaymentButton() {
      const mainForm = this.getMainForm();
      if (!mainForm) return null;

      const container = mainForm.closest('product-form, .product-form, .product__info-container, #MainProduct-' + this.sectionId);
      if (!container) return null;

      return container.querySelector('.shopify-payment-button__button, .shopify-payment-button button');
    }

    getStickyHeader() {
      const source = this.parts.buy || this.parts.quantity || this.parts.variant;
      return source ? source.closest('.product-sticky-header') : null;
    }

    setStickyHeaderVisibility(shouldShow) {
      const stickyHeader = this.getStickyHeader();
      if (!stickyHeader) return;
      stickyHeader.style.display = shouldShow ? '' : 'none';
    }

    bindDocumentEvents() {
      document.addEventListener('change', this.boundHandleDocumentChange);
      document.addEventListener('input', this.boundHandleDocumentChange);
      document.addEventListener('click', this.boundHandleDocumentClick);
    }

    handleDocumentChange(event) {
      const target = event.target;
      if (!target || isInsideSticky(target)) return;

      if (
        target.id === `Quantity-${this.sectionId}` ||
        target.id === `Option-${this.sectionId}-0` ||
        target.closest(`#variant-radios-${cssEscape(this.sectionId)}`) ||
        target.closest(`#variant-selects-${cssEscape(this.sectionId)}`) ||
        target.closest(`#${cssEscape(this.getMainFormId())}`)
      ) {
        this.queueSync();
      }
    }

    handleDocumentClick(event) {
      const target = event.target;
      if (!target || isInsideSticky(target)) return;

      if (target.closest(`#ProductSubmitButton-${cssEscape(this.sectionId)}`)) {
        this.queueSync();
      }
    }

    handleIntersection(entries) {
      const entry = entries[0];
      if (!entry) return;

      // Hide sticky header when the real add to cart button is visible.
      this.setStickyHeaderVisibility(!entry.isIntersecting);
    }

    observeMainSection() {
      const mainSection = this.getMainSection();
      if (!mainSection) return;

      this.mainObserver = new MutationObserver(() => this.queueSync());
      this.mainObserver.observe(mainSection, {
        subtree: true,
        childList: true,
        attributes: true,
        characterData: true,
      });
    }

    observeStickyRoots() {
      const roots = [this.parts.variant, this.parts.quantity, this.parts.buy].filter(Boolean);
      if (!roots.length) return;

      this.stickyObserver = new MutationObserver(() => this.queueSync());
      roots.forEach((root) => {
        this.stickyObserver.observe(root, {
          subtree: true,
          childList: true,
        });
      });
    }

    observeMainSubmitButtonVisibility() {
      const mainSubmitButton = this.getMainSubmitButton();

      if (!mainSubmitButton) {
        if (this.submitVisibilityObserver) {
          this.submitVisibilityObserver.disconnect();
        }
        this.submitVisibilityObserver = null;
        this.observedSubmitButton = null;
        this.setStickyHeaderVisibility(true);
        return;
      }

      if (this.observedSubmitButton === mainSubmitButton && this.submitVisibilityObserver) {
        return;
      }

      if (this.submitVisibilityObserver) {
        this.submitVisibilityObserver.disconnect();
      }

      this.observedSubmitButton = mainSubmitButton;
      this.submitVisibilityObserver = new IntersectionObserver(this.boundHandleIntersection, {
        root: null,
        threshold: 0.01,
      });

      this.submitVisibilityObserver.observe(mainSubmitButton);
    }

    queueSync() {
      if (this.syncQueued) return;
      this.syncQueued = true;
      window.requestAnimationFrame(() => {
        this.syncQueued = false;
        this.syncFromMain();
      });
    }

    bindStickyVariantControls() {
      if (!this.parts.variant) return;

      this.parts.variant.addEventListener('change', (event) => {
        const target = event.target;
        if (!target) return;

        if (target.matches('[data-sticky-option-index]')) {
          const optionIndex = Number(target.dataset.stickyOptionIndex);
          const value = target.value;
          this.syncStickyOptionToMain(optionIndex, value);
        }
      });
    }

    bindStickyQuantityControls() {
      if (!this.parts.quantity) return;

      const stickyInput = this.parts.quantity.querySelector('[data-sticky-quantity-input]');
      const minusButton = this.parts.quantity.querySelector('[data-sticky-quantity-minus]');
      const plusButton = this.parts.quantity.querySelector('[data-sticky-quantity-plus]');

      if (!stickyInput) return;

      const applyQuantity = (rawValue) => {
        const nextValue = clampToRules(rawValue, stickyInput.min, stickyInput.max, stickyInput.step);
        stickyInput.value = nextValue;
        const mainInput = this.getMainQuantityInput();
        if (mainInput) {
          mainInput.value = nextValue;
          triggerFieldEvents(mainInput);
        }
        this.queueSync();
      };

      stickyInput.addEventListener('change', () => applyQuantity(stickyInput.value));
      stickyInput.addEventListener('blur', () => applyQuantity(stickyInput.value));

      if (minusButton) {
        minusButton.addEventListener('click', () => {
          const currentValue = Number(stickyInput.value || stickyInput.min || 1);
          const step = Number(stickyInput.step || 1);
          applyQuantity(currentValue - step);
        });
      }

      if (plusButton) {
        plusButton.addEventListener('click', () => {
          const currentValue = Number(stickyInput.value || stickyInput.min || 1);
          const step = Number(stickyInput.step || 1);
          applyQuantity(currentValue + step);
        });
      }
    }

    bindStickyButtons() {
      if (!this.parts.buy) return;

      const stickySubmitButton = this.parts.buy.querySelector('[data-sticky-submit-button]');
      const stickyPaymentButton = this.parts.buy.querySelector('[data-sticky-payment-button]');

      if (stickySubmitButton) {
        stickySubmitButton.addEventListener('click', () => {
          const mainSubmit = this.getMainSubmitButton();
          if (!mainSubmit || mainSubmit.disabled) return;
          mainSubmit.click();
          this.queueSync();
        });
      }

      if (stickyPaymentButton) {
        stickyPaymentButton.addEventListener('click', () => {
          const mainPaymentButton = this.getMainPaymentButton();
          if (!mainPaymentButton || mainPaymentButton.disabled) return;
          mainPaymentButton.click();
          this.queueSync();
        });
      }
    }

    syncStickyOptionToMain(optionIndex, value) {
      const mainVariantSelects = this.getMainVariantSelects();
      const mainVariantRadios = this.getMainVariantRadios();

      if (mainVariantSelects) {
        const mainSelect = mainVariantSelects.querySelector(`#Option-${cssEscape(this.sectionId)}-${optionIndex}`);
        if (mainSelect) {
          mainSelect.value = value;
          triggerFieldEvents(mainSelect);
        }
      } else if (mainVariantRadios) {
        const fieldsets = mainVariantRadios.querySelectorAll('fieldset');
        const fieldset = fieldsets[optionIndex];
        if (!fieldset) return;

        const matchingInput = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find((input) => input.value === value);
        if (matchingInput) {
          matchingInput.checked = true;
          triggerFieldEvents(matchingInput);
        }
      }

      this.queueSync();
    }

    syncVariantPickerFromMain() {
      if (!this.parts.variant) return;

      const mainVariantSelects = this.getMainVariantSelects();
      const mainVariantRadios = this.getMainVariantRadios();

      if (mainVariantSelects) {
        this.parts.variant.querySelectorAll('.sticky-product-form__select').forEach((stickySelect) => {
          const optionIndex = Number(stickySelect.dataset.stickyOptionIndex);
          const mainSelect = mainVariantSelects.querySelector(`#Option-${cssEscape(this.sectionId)}-${optionIndex}`);
          if (!mainSelect) return;

          if (stickySelect.innerHTML !== mainSelect.innerHTML) {
            stickySelect.innerHTML = mainSelect.innerHTML;
          }
          stickySelect.value = mainSelect.value;
          stickySelect.disabled = mainSelect.disabled;
        });

        return;
      }

      if (mainVariantRadios) {
        const mainFieldsets = mainVariantRadios.querySelectorAll('fieldset');
        this.parts.variant.querySelectorAll('fieldset[data-sticky-option-index]').forEach((stickyFieldset) => {
          const optionIndex = Number(stickyFieldset.dataset.stickyOptionIndex);
          const mainFieldset = mainFieldsets[optionIndex];
          if (!mainFieldset) return;

          const mainInputs = Array.from(mainFieldset.querySelectorAll('input[type="radio"]'));
          stickyFieldset.querySelectorAll('input[type="radio"]').forEach((stickyInput) => {
            const matchingInput = mainInputs.find((input) => input.value === stickyInput.value);
            const stickyLabel = this.parts.variant.querySelector(`label[for="${cssEscape(stickyInput.id)}"]`);

            if (!matchingInput) {
              stickyInput.disabled = true;
              stickyInput.checked = false;
              if (stickyLabel) stickyLabel.classList.add('disabled');
              return;
            }

            stickyInput.disabled = matchingInput.disabled;
            stickyInput.checked = matchingInput.checked;

            if (stickyLabel) {
              stickyLabel.classList.toggle('disabled', matchingInput.disabled);
              stickyLabel.setAttribute('aria-disabled', matchingInput.disabled ? 'true' : 'false');
            }
          });
        });
      }
    }

    syncQuantityFromMain() {
      if (!this.parts.quantity) return;

      const stickyInput = this.parts.quantity.querySelector('[data-sticky-quantity-input]');
      const mainInput = this.getMainQuantityInput();
      if (!stickyInput || !mainInput) return;

      stickyInput.value = mainInput.value;
      stickyInput.min = mainInput.min || stickyInput.min;
      stickyInput.step = mainInput.step || stickyInput.step;
      stickyInput.disabled = mainInput.disabled;

      if (mainInput.max) {
        stickyInput.max = mainInput.max;
        stickyInput.setAttribute('data-max', mainInput.max);
      } else {
        stickyInput.removeAttribute('max');
        stickyInput.removeAttribute('data-max');
      }

      if (mainInput.min) {
        stickyInput.setAttribute('data-min', mainInput.min);
      }
    }

    syncButtonsFromMain() {
      if (!this.parts.buy) return;

      const stickySubmitButton = this.parts.buy.querySelector('[data-sticky-submit-button]');
      const stickySpinner = this.parts.buy.querySelector('[data-sticky-submit-spinner]');
      const stickyPaymentButton = this.parts.buy.querySelector('[data-sticky-payment-button]');
      const stickyErrorWrapper = this.parts.buy.querySelector('[data-sticky-error-wrapper]');
      const stickyErrorMessage = this.parts.buy.querySelector('[data-sticky-error-message]');

      const mainSubmitButton = this.getMainSubmitButton();
      const mainErrorWrapper = this.getMainErrorWrapper();
      const mainPaymentButton = this.getMainPaymentButton();

      if (stickySubmitButton && mainSubmitButton) {
        stickySubmitButton.disabled = mainSubmitButton.disabled;

        const stickyText = getStickySubmitTextElement(stickySubmitButton);
        const mainLabel = getButtonLabel(mainSubmitButton);

        if (stickyText) {
          stickyText.textContent = mainLabel;
        }

        const mainSpinner = mainSubmitButton.querySelector('.loading-overlay__spinner');
        if (stickySpinner && mainSpinner) {
          stickySpinner.classList.toggle('hidden', mainSpinner.classList.contains('hidden'));
        }
      }

      if (stickyPaymentButton) {
        const shouldShowPayment = Boolean(mainPaymentButton);
        const paymentWrapper = stickyPaymentButton.closest('.shopify-payment-button');
        if (paymentWrapper) {
          paymentWrapper.hidden = !shouldShowPayment;
        }
        stickyPaymentButton.disabled = !shouldShowPayment || Boolean(mainPaymentButton && mainPaymentButton.disabled);
      }

      if (stickyErrorWrapper && stickyErrorMessage) {
        if (mainErrorWrapper && !mainErrorWrapper.hasAttribute('hidden')) {
          stickyErrorWrapper.removeAttribute('hidden');
          const mainMessage = mainErrorWrapper.querySelector('.product-form__error-message');
          stickyErrorMessage.textContent = mainMessage ? mainMessage.textContent.trim() : '';
        } else {
          stickyErrorWrapper.setAttribute('hidden', 'hidden');
          stickyErrorMessage.textContent = '';
        }
      }
    }

    syncFromMain() {
      this.syncVariantPickerFromMain();
      this.syncQuantityFromMain();
      this.syncButtonsFromMain();
      this.observeMainSubmitButtonVisibility();
    }
  }

  function buildGroups() {
    const groups = new Map();

    document.querySelectorAll(STICKY_SELECTOR).forEach((element) => {
      const sectionId = element.dataset.stickySectionId;
      if (!sectionId) return;

      if (!groups.has(sectionId)) {
        groups.set(sectionId, { sectionId, variant: null, quantity: null, buy: null });
      }

      const group = groups.get(sectionId);
      const type = element.dataset.stickyProductUi;
      if (type === 'variant-picker') group.variant = element;
      if (type === 'quantity-picker') group.quantity = element;
      if (type === 'buy-buttons') group.buy = element;
    });

    return groups;
  }

  function initStickyProductFormProxies() {
    buildGroups().forEach((parts, sectionId) => {
      if (!parts.__instance) {
        const instance = new StickyProductFormProxy(sectionId, parts);
        parts.__instance = instance;
        instance.init();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStickyProductFormProxies);
  } else {
    initStickyProductFormProxies();
  }
})();